import React, { FC, useEffect, useState } from 'react';

import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next/pages';

import { Button, Form, FormInstance, Input, message, Select, Skeleton } from 'antd';
import { DefaultOptionType } from 'antd/es/select';

import { getTemperatures } from '@/api/reference-data';
import { LocationSelect } from '@/components/LocationSelect/LocationSelect';
import { parseQueryParamsForFindWarehouseFilter } from '@/components/PersonalAccountWarehouses/FindWarehouse/SearchForm/utils';
import { ChevronDown, ChevronUp } from '@/components/SearchFishToBuy/MainFilters/icons';
import useAsyncEffect from '@/hooks/use-async-effect';
import useMobile from '@/hooks/use-mobile';
import { TranslationNamespace } from '@/i18n';
import { TemperatureRegime } from '@/types/temperature-regime';

import { MainWarehousesFiltersForm } from '../../SearchWarehouses';

import styles from './MainWarehouseFilters.module.scss';

type Props = {
	mainFilters: FormInstance<MainWarehousesFiltersForm>;
	isDisabledSubmitBtn: boolean;
	onSubmit: () => void;
};

export const MainWarehouseFilters: FC<Props> = ({ isDisabledSubmitBtn, mainFilters, onSubmit }) => {
	const { t } = useTranslation(TranslationNamespace.WarehouseSearch);
	const { isMobile } = useMobile();
	const [isOpen, setIsOpen] = useState(!isMobile);
	const router = useRouter();
	const [temperatures, setTemperatures] = useState<TemperatureRegime[]>(null);
	const [isTemperaturesLoading, setIsTemperaturesLoading] = useState(true);

	useEffect(() => {
		setIsOpen(!isMobile);
	}, [isMobile]); //problem with initial value isMobile = false for first render and we need to setup it

	const handleExpandCollapse = () => {
		setIsOpen(!isOpen);
	};

	useAsyncEffect(async () => {
		try {
			const temperatures = (await getTemperatures(false)) as TemperatureRegime[];
			setTemperatures(temperatures);
		} catch {
			message.error(t('temperature-load-error'));
		} finally {
			setIsTemperaturesLoading(false);
		}
	}, []);

	if (isTemperaturesLoading) {
		return (
			<div className={styles.skeletonWrapper}>
				<Skeleton
					active
					title={{ width: '100%' }}
					paragraph={{ rows: 1, className: styles.skeletonItem }}
				/>
			</div>
		);
	}

	return (
		<div className={styles.container}>
			{isMobile && (
				<div className={styles.mobileTitle} onClick={handleExpandCollapse}>
					<span>{t('warehouses-search-filters-heading.product-filter')}</span>
					{isOpen ? <ChevronUp /> : <ChevronDown />}
				</div>
			)}
			{isOpen && (
				<Form<MainWarehousesFiltersForm>
					layout="vertical"
					className={styles.form}
					form={mainFilters}
					initialValues={parseQueryParamsForFindWarehouseFilter(router.query)}
					onFinish={onSubmit}
				>
					<div className={styles.locationFormItemWrapper}>
						<Form.Item
							label={!isMobile && t('warehouses-search-filters-heading.warehouse-location')}
							className={styles.formItem}
							name="location"
						>
							<LocationSelect
								placeholder={
									isMobile
										? t('warehouses-search-filters-heading.warehouse-location')
										: t('warehouses-search-filters-heading.nearest-city')
								}
								inputArea="input"
								virtual={false}
								toBound="city"
								fromBound="city"
								valueField="value"
								onChange={(_, option) => {
									mainFilters.setFieldValue(
										'longitude',
										(option as DefaultOptionType)?.additionalInfo?.geo_lon,
									);
									mainFilters.setFieldValue(
										'latitude',
										(option as DefaultOptionType)?.additionalInfo?.geo_lat,
									);
								}}
							/>
						</Form.Item>
					</div>
					{/* Фэйковые поля для range picker */}
					<Form.Item name="longitude" noStyle>
						<Input type="hidden" />
					</Form.Item>
					<Form.Item name="latitude" noStyle>
						<Input type="hidden" />
					</Form.Item>

					<div className={styles.distanceFormItemWrapper}>
						<Form.Item
							label={!isMobile && t('warehouses-search-filters-heading.search-radius')}
							className={styles.formItem}
							name="max_distance"
						>
							<Input
								placeholder={t('warehouses-search-filters-heading.search-radius-placeholder')}
								size="large"
								allowClear
							/>
						</Form.Item>
					</div>

					<div className={styles.tempFormItemWrapper}>
						<Form.Item
							label={!isMobile && t('warehouses-search-filters-heading.temperature-short')}
							className={styles.formItem}
							name="temp_regime"
						>
							<Select
								placeholder={t('warehouses-search-filters-heading.temperature-placeholder')}
								size="large"
								allowClear
								options={temperatures
									?.sort((a, b) => a.degree - b.degree)
									.map(({ id, degree }) => ({
										label: `${degree}° C`,
										value: id,
									}))}
							/>
						</Form.Item>
					</div>

					<div className={styles.weightFormItemWrapper}>
						<Form.Item
							label={!isMobile && t('warehouses-search-filters-heading.cargo-weight-gross')}
							className={styles.formItem}
							name="free_volume"
						>
							<Input
								placeholder={t('warehouses-search-filters-heading.cargo-weight-placeholder')}
								size="large"
								allowClear
							/>
						</Form.Item>
					</div>

					<Button
						className={styles.button}
						type="primary"
						size="middle"
						htmlType="submit"
						block
						disabled={isDisabledSubmitBtn}
					>
						{t('warehouses-search-filters-heading.find')}
					</Button>
				</Form>
			)}
		</div>
	);
};
